import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fileUrl, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { useCan } from '../lib/auth';
import { Button, Callout, Checkbox, Dialog, Empty, ErrorState, Field, Input, Ltr, Select, SkeletonRows, useConfirm, useToast, useDebounced } from '../components/ui';
import { IconUpload } from '../components/icons';
import { QuickProductDialog } from '../components/ProfitGaps';
import { shrinkImage } from '../components/ListReader';
import { qtyText } from './Inventory';

type RProduct = { productId: number; sku: string; name: string; unit: string | null; unitsPerPack: number | null; cost: number | null; counted: boolean };
type RLine = {
  index: number;
  supplierSku: string | null;
  text: string;
  quantity: number | null;
  unit: string | null;
  lineTotal: number | null;
  productId: number | null;
  factor: number;
  confidence: number;
  source: 'learned' | 'ai' | 'local' | null;
  reason: string | null;
  candidates: number[];
};
type Receipt = {
  id: number;
  status: 'read' | 'applied' | 'cancelled';
  supplier: { id: number | null; name: string | null; companyNumber: string | null };
  docType: string | null;
  docNumber: string | null;
  docDate: string | null;
  totalBeforeVat: number | null;
  fileId: number | null;
  fileMime: string | null;
  appliedAt: string | null;
  summary: { lines: number; units: number; value: number | null; costChanges: Array<{ productId: number; before: number | null; after: number }> } | null;
  movements: number;
  duplicates: Array<{ id: number; appliedAt: string }>;
  lines: RLine[];
  products: Record<number, RProduct>;
};
type ReceiptRow = { id: number; supplier_name: string | null; doc_number: string | null; doc_date: string | null; status: string; lines: number; created_at: string; applied_at: string | null; user_name: string | null; error: string | null };

const STATE: Record<string, { label: string; cls: string }> = {
  read: { label: 'ממתינה לאישור', cls: 'warn' },
  applied: { label: 'נקלטה למלאי', cls: 'ok' },
  cancelled: { label: 'בוטלה', cls: 'outline' },
};

/** Goods from a supplier: upload a photo / PDF of its invoice or delivery note, and the receipts so far. */
export function ReceiptsPage() {
  const { data, error, refetch } = useQuery({ queryKey: ['receipts'], queryFn: () => api<{ items: ReceiptRow[] }>('/api/inventory/receipts') });
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', await shrinkImage(file), file.type === 'application/pdf' ? file.name : 'receipt.jpg');
      const r = await api<{ id: number }>('/api/inventory/receipts/read', { form });
      navigate(`/inventory/receipts/${r.id}`);
    } catch (e) {
      toast.error(e);
      refetch();
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/inventory">מלאי</Link> / קבלת סחורה</div>
          <h1>קבלת סחורה</h1>
          <div className="sub">מצלמים או מעלים חשבונית / תעודת משלוח של ספק. הסוכן קורא את השורות ומתאים למוצרים בקטלוג; בודקים ומאשרים — והמלאי עולה, והעלות מתעדכנת למחיר האחרון ששולם.</div>
        </div>
      </div>
      <div className="stack">
        <section className="panel panel-body">
          <input
            ref={input}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
          <div className="row wrap" style={{ gap: 12 }}>
            <Button variant="primary" loading={busy} onClick={() => input.current?.click()}>
              <IconUpload /> {busy ? 'קורא את המסמך… (עד דקה)' : 'צילום / העלאת חשבונית ספק'}
            </Button>
            <span className="muted small">תמונה (בטלפון — אפשר לצלם ישר) או PDF. עמוד אחד בכל פעם; מסמך של כמה עמודים — כ-PDF.</span>
          </div>
        </section>

        <section className="panel">
          {error ? (
            <ErrorState error={error} onRetry={() => refetch()} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>ספק</th>
                    <th>מסמך</th>
                    <th className="hide-sm">תאריך</th>
                    <th className="num">שורות</th>
                    <th>מצב</th>
                    <th className="hide-sm">מי</th>
                  </tr>
                </thead>
                <tbody>
                  {!data ? (
                    <SkeletonRows rows={4} cols={6} />
                  ) : data.items.length === 0 ? (
                    <tr><td colSpan={6}><Empty title="עוד לא נקלטה סחורה" /></td></tr>
                  ) : (
                    data.items.map((r) => (
                      <tr key={r.id} className="clickable" onClick={() => navigate(`/inventory/receipts/${r.id}`)}>
                        <td className="primary">{r.supplier_name ?? <span className="faint">—</span>}{r.error ? <span className="sub" style={{ color: 'var(--bad)' }}>{r.error}</span> : null}</td>
                        <td><Ltr>{r.doc_number ?? '—'}</Ltr></td>
                        <td className="hide-sm">{fmtDate(r.doc_date ?? r.created_at)}</td>
                        <td className="num tabular">{fmtNumber(r.lines)}</td>
                        <td><span className={`badge ${STATE[r.status]?.cls ?? ''}`}>{r.error ? 'הקריאה נכשלה' : STATE[r.status]?.label}</span></td>
                        <td className="hide-sm muted">{r.user_name ?? '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

type Draft = { include: boolean; productId: number | null; quantity: string; unitCost: string; updateCost: boolean };
const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Check a read receipt next to the photo, then approve: stock up, costs updated, every choice learned. */
export function ReceiptPage() {
  const id = Number(useParams().id);
  const { data, error, refetch } = useQuery({ queryKey: ['receipt', id], queryFn: () => api<Receipt>(`/api/inventory/receipts/${id}`), refetchOnWindowFocus: false });
  if (error) return <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>;
  if (!data) return <div className="panel panel-body"><span className="spinner" /></div>;
  return <ReceiptEditor key={`${data.id}:${data.status}`} r={data} />;
}

function ReceiptEditor({ r }: { r: Receipt }) {
  const can = useCan();
  const showCost = can('profit.view');
  const open = r.status === 'read';
  const [products, setProducts] = useState<Record<number, RProduct>>(r.products);
  const [supplierName, setSupplierName] = useState(r.supplier.name ?? '');
  const [companyNumber, setCompanyNumber] = useState(r.supplier.companyNumber ?? '');
  const [docNumber, setDocNumber] = useState(r.docNumber ?? '');
  const [docDate, setDocDate] = useState(r.docDate ?? '');
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    r.lines.map((l) => {
      const qty = l.quantity != null ? round2(l.quantity * (l.factor || 1)) : null;
      return {
        include: l.productId != null,
        productId: l.productId,
        quantity: qty != null ? String(qty) : '',
        unitCost: l.lineTotal != null && qty ? String(round2(l.lineTotal / qty)) : '',
        updateCost: true,
      };
    }),
  );
  const [search, setSearch] = useState<number | null>(null);
  const [adding, setAdding] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<Receipt['summary'] | null>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const set = (i: number, patch: Partial<Draft>) => setDrafts((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const choose = (i: number, p: RProduct) => {
    setProducts((ps) => ({ ...ps, [p.productId]: p }));
    const l = r.lines[i]!;
    const qty = num(drafts[i]!.quantity) ?? l.quantity ?? null;
    set(i, { productId: p.productId, include: true, quantity: qty != null ? String(qty) : '', unitCost: l.lineTotal != null && qty ? String(round2(l.lineTotal / qty)) : drafts[i]!.unitCost });
  };
  const chosen = drafts.filter((d) => d.include && d.productId && (num(d.quantity) ?? 0) > 0);
  const invalid = drafts.some((d) => d.include && (!d.productId || !((num(d.quantity) ?? 0) > 0)));

  const apply = async (force = false) => {
    setBusy('apply');
    try {
      const res = await api<NonNullable<Receipt['summary']>>(`/api/inventory/receipts/${r.id}/apply`, {
        body: {
          supplierName: supplierName.trim(),
          supplierCompanyNumber: companyNumber.trim() || null,
          docNumber: docNumber.trim() || null,
          docDate: docDate || null,
          force,
          lines: drafts
            .map((d, i) => ({ d, i }))
            .filter(({ d }) => d.include && d.productId && (num(d.quantity) ?? 0) > 0)
            .map(({ d, i }) => ({ index: r.lines[i]!.index, productId: d.productId!, quantity: num(d.quantity)!, unitCost: showCost ? num(d.unitCost) : null, updateCost: d.updateCost, learn: true })),
        },
      });
      setResult(res);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['receipts'] });
      qc.invalidateQueries({ queryKey: ['profit'] });
      toast.success(`נקלט למלאי — ${fmtNumber(res.lines)} מוצרים`);
    } catch (e) {
      const dup = (e as { code?: string; message?: string }).code === 'conflict' && /כבר נקלט/.test((e as Error).message);
      if (dup && (await confirm({ title: 'המסמך כבר נקלט', body: `${(e as Error).message}. לקלוט בכל זאת? (המלאי יעלה פעמיים)`, danger: true, confirmLabel: 'לקלוט שוב' }))) {
        setBusy(null);
        return apply(true);
      }
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const cancel = async () => {
    const applied = r.status === 'applied';
    if (!(await confirm({ title: applied ? 'לבטל את הקבלה?' : 'לבטל את הקריאה?', body: applied ? 'הכמויות שנקלטו יירדו מהמלאי. העלויות שעודכנו נשארות.' : 'שום דבר לא נקלט למלאי.', danger: true, confirmLabel: 'ביטול' }))) return;
    setBusy('cancel');
    try {
      await api(`/api/inventory/receipts/${r.id}/cancel`, { body: {} });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['receipts'] });
      navigate('/inventory/receipts');
    } catch (e) {
      toast.error(e);
      setBusy(null);
    }
  };

  const summary = result ?? r.summary;
  const pending = open && !result;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/inventory">מלאי</Link> / <Link to="/inventory/receipts">קבלת סחורה</Link></div>
          <h1>{r.supplier.name ?? 'חשבונית ספק'}{r.docNumber ? ` · ${r.docNumber}` : ''}</h1>
          <div className="sub"><span className={`badge ${STATE[result ? 'applied' : r.status]!.cls}`}>{STATE[result ? 'applied' : r.status]!.label}</span>{r.appliedAt ? ` · ${fmtDate(r.appliedAt)}` : ''}</div>
        </div>
        <div className="row wrap">
          {r.status !== 'cancelled' && !result ? <Button variant="danger" loading={busy === 'cancel'} onClick={cancel}>{r.status === 'applied' ? 'ביטול הקבלה' : 'ביטול'}</Button> : null}
        </div>
      </div>

      <div className="receipt-layout">
        <aside className="panel receipt-file">
          {r.fileId ? (
            r.fileMime === 'application/pdf' ? (
              <iframe title="המסמך" src={fileUrl(r.fileId)} style={{ width: '100%', height: '100%', minHeight: 520, border: 0 }} />
            ) : (
              <a href={fileUrl(r.fileId)} target="_blank" rel="noreferrer"><img src={fileUrl(r.fileId)} alt="המסמך מהספק" style={{ width: '100%', display: 'block' }} /></a>
            )
          ) : (
            <Empty title="אין קובץ" />
          )}
        </aside>

        <div className="stack" style={{ minWidth: 0 }}>
          {summary && !pending ? (
            <Callout tone="ok" title="נקלט למלאי">
              {fmtNumber(summary.lines)} מוצרים, {qtyText(summary.units)} יחידות{summary.value != null && showCost ? ` בשווי ${fmtMoney(summary.value)}` : ''}.
              {summary.costChanges.length ? (
                <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                  {summary.costChanges.map((c) => (
                    <li key={c.productId}>
                      {products[c.productId]?.name ?? `מוצר ${c.productId}`}: עלות {c.before == null ? 'חדשה' : `${fmtMoney(c.before)} ←`} {fmtMoney(c.after)}
                      {c.before != null && c.after > c.before ? <strong style={{ color: 'var(--bad)' }}> (עלתה {Math.round(((c.after - c.before) / c.before) * 100)}%)</strong> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {summary.costChanges.some((c) => c.before != null && c.after > c.before) ? (
                <div style={{ marginTop: 6 }}>עלויות עלו — כדאי לבדוק ב<Link to="/profit">רווחיות</Link> אילו לקוחות ירדו מתחת ליעד.</div>
              ) : null}
            </Callout>
          ) : null}

          {r.duplicates.length && pending ? (
            <Callout tone="bad" title="המסמך הזה כבר נקלט">
              אותו מספר מסמך מאותו ספק נקלט כבר{' '}
              {r.duplicates.map((d) => <Link key={d.id} to={`/inventory/receipts/${d.id}`}>(#{d.id}, {fmtDate(d.appliedAt)}) </Link>)}
              — קליטה נוספת תעלה את המלאי פעמיים.
            </Callout>
          ) : null}

          <section className="panel panel-body">
            <div className="row wrap" style={{ gap: 12, alignItems: 'flex-end' }}>
              <Field label="ספק" required>
                <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} disabled={!pending} style={{ width: 220 }} />
              </Field>
              <Field label="ח.פ / ע.מ">
                <Input value={companyNumber} onChange={(e) => setCompanyNumber(e.target.value)} disabled={!pending} ltr style={{ width: 130 }} />
              </Field>
              <Field label={r.docType ?? 'מספר מסמך'}>
                <Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} disabled={!pending} ltr style={{ width: 130 }} />
              </Field>
              <Field label="תאריך">
                <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} disabled={!pending} style={{ width: 150 }} />
              </Field>
              {showCost && r.totalBeforeVat != null ? <div className="muted small">סה״כ במסמך לפני מע״מ: <strong>{fmtMoney(r.totalBeforeVat)}</strong></div> : null}
            </div>
          </section>

          <section className="panel">
            <div className="table-wrap">
              <table className="table receipt-lines">
                <thead>
                  <tr>
                    <th style={{ width: 28 }} />
                    <th>בחשבונית הספק</th>
                    <th>מוצר אצלנו</th>
                    <th className="num">כמות (יח׳ קטלוג)</th>
                    {showCost ? <th className="num">עלות ליח׳</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {r.lines.map((l, i) => {
                    const d = drafts[i]!;
                    const p = d.productId ? products[d.productId] : null;
                    const costNow = p?.cost ?? null;
                    const uc = num(d.unitCost);
                    const change = costNow != null && uc != null && Math.abs(uc - costNow) > 0.01 ? (uc - costNow) / costNow : null;
                    return (
                      <tr key={l.index} style={!d.include ? { opacity: 0.55 } : undefined}>
                        <td><Checkbox checked={d.include} onChange={(v) => set(i, { include: v })} disabled={!pending}>{''}</Checkbox></td>
                        <td>
                          <span className="strong">{l.text}</span>
                          <span className="sub">
                            {l.supplierSku ? <><Ltr>{l.supplierSku}</Ltr> · </> : null}
                            {l.quantity != null ? `${qtyText(l.quantity)} ${l.unit ?? ''}` : 'בלי כמות'}
                            {showCost && l.lineTotal != null ? ` · ${fmtMoney(l.lineTotal)}` : ''}
                          </span>
                        </td>
                        <td style={{ minWidth: 220 }}>
                          {pending ? (
                            <>
                              <Select
                                value={d.productId ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === 'search') setSearch(i);
                                  else if (v === 'new') setAdding(i);
                                  else if (v === '') set(i, { productId: null, include: false });
                                  else choose(i, products[Number(v)]!);
                                }}
                                aria-label="מוצר"
                              >
                                <option value="">— לא לקלוט —</option>
                                {[...new Set([...(d.productId ? [d.productId] : []), ...l.candidates])].filter((cid) => products[cid]).map((cid) => (
                                  <option key={cid} value={cid}>{products[cid]!.name} ({products[cid]!.sku})</option>
                                ))}
                                <option value="search">🔍 חיפוש מוצר אחר…</option>
                                <option value="new">+ מוצר חדש בקטלוג</option>
                              </Select>
                              <span className="sub">
                                {l.source === 'learned' ? <span style={{ color: 'var(--ok)' }}>✓ נלמד מהספק</span> : l.productId && l.confidence < 0.7 ? <span style={{ color: 'var(--warn)' }}>לבדוק</span> : null}
                                {l.reason ? ` ${l.reason}` : ''}
                                {p && !p.counted ? <span className="muted"> · עוד לא נספר — המלאי יתחיל מהכמות שמתקבלת</span> : null}
                              </span>
                            </>
                          ) : p ? (
                            <span>{p.name}<span className="sub"><Ltr>{p.sku}</Ltr></span></span>
                          ) : (
                            <span className="faint">לא נקלט</span>
                          )}
                        </td>
                        <td className="num">
                          <Input value={d.quantity} onChange={(e) => set(i, { quantity: e.target.value })} disabled={!pending || !d.include} inputMode="decimal" ltr style={{ width: 80 }} aria-label="כמות" />
                          {p?.unitsPerPack ? <span className="sub">חבילה של {p.unitsPerPack}</span> : null}
                        </td>
                        {showCost ? (
                          <td className="num">
                            <Input value={d.unitCost} onChange={(e) => set(i, { unitCost: e.target.value })} disabled={!pending || !d.include} inputMode="decimal" ltr style={{ width: 90 }} aria-label="עלות ליחידה" />
                            <span className="sub">
                              {costNow != null ? `עכשיו ${fmtMoney(costNow)}` : 'אין עלות'}
                              {change != null ? <strong style={{ color: change > 0 ? 'var(--bad)' : 'var(--ok)' }}> {change > 0 ? '▲' : '▼'}{Math.abs(Math.round(change * 100))}%</strong> : null}
                            </span>
                            {pending && d.include && change != null ? (
                              <Checkbox checked={d.updateCost} onChange={(v) => set(i, { updateCost: v })}>לעדכן עלות</Checkbox>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pending ? (
              <div className="panel-body row wrap" style={{ gap: 10, borderTop: '1px solid var(--line)' }}>
                <span className="muted small">
                  {fmtNumber(chosen.length)} מתוך {fmtNumber(r.lines.length)} שורות ייקלטו
                  {showCost ? ` · ${fmtMoney(round2(chosen.reduce((s, d) => s + (num(d.unitCost) ?? 0) * (num(d.quantity) ?? 0), 0)))} לפני מע״מ` : ''}
                </span>
                <span className="spacer" />
                <Button variant="primary" loading={busy === 'apply'} disabled={!chosen.length || invalid || !supplierName.trim()} onClick={() => apply()}>
                  אישור — קליטה למלאי
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {search != null ? <ProductSearch onClose={() => setSearch(null)} onPick={(p) => { choose(search, p); setSearch(null); }} initial={r.lines[search]!.text} /> : null}
      {adding != null ? (
        <QuickProductDialog
          text={r.lines[adding]!.text}
          canCost={showCost}
          onClose={() => setAdding(null)}
          onCreated={(p) => {
            choose(adding, { productId: p.id, sku: p.sku, name: p.name, unit: null, unitsPerPack: null, cost: p.cost, counted: false });
            setAdding(null);
          }}
        />
      ) : null}
    </>
  );
}

/** Pick any catalog product for a supplier line. */
function ProductSearch({ initial, onPick, onClose }: { initial: string; onPick: (p: RProduct) => void; onClose: () => void }) {
  const [q, setQ] = useState(initial);
  const dq = useDebounced(q, 300);
  const { data, isFetching } = useQuery({ queryKey: ['inventory', 'product-search', dq], queryFn: () => api<{ items: RProduct[] }>(`/api/inventory/products${qs({ q: dq })}`), enabled: dq.trim().length >= 2 });
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.select(), []);
  return (
    <Dialog open onClose={onClose} title="חיפוש מוצר בקטלוג">
      <div className="stack">
        <Input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} placeholder="שם או מק״ט" autoFocus />
        {isFetching ? <span className="spinner" /> : null}
        <ul className="pick-index">
          {(data?.items ?? []).map((p) => (
            <li key={p.productId}>
              <button type="button" className="pick-index-row" style={{ width: '100%', background: 'none', border: 0, borderBottom: '1px solid var(--line)', textAlign: 'start', cursor: 'pointer', font: 'inherit' }} onClick={() => onPick(p)}>
                <span className="pick-index-main">
                  <span>{p.name}</span>
                  <span className="muted small"><Ltr>{p.sku}</Ltr>{p.unitsPerPack ? ` · חבילה של ${p.unitsPerPack}` : ''}{p.cost != null ? ` · עלות ${fmtMoney(p.cost)}` : ''}</span>
                </span>
              </button>
            </li>
          ))}
          {data && !data.items.length ? <li className="muted small" style={{ padding: 10 }}>לא נמצא — אפשר להוסיף מוצר חדש</li> : null}
        </ul>
      </div>
    </Dialog>
  );
}
